import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import HomePage from "./HomePage";
import CompanyManagement from "./CompanyManagement";
import { PrivateRoute, AdminRoute } from "./PrivateRoute";
import DeploymentLogsPage from "./DeploymentLogsPage";
import StatisticsPage from "./StatisticsPage";
import { ThemeToggleProvider } from "./Context/ThemeContext";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MenuProvider } from "./Context/MenuVisibility";
import ApplicationManagementPage from "./ApplicationManagementPage";
import DeviceManagement from "./DeviceManagement";
import Settings from "./Settings";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { NotificationProvider, useNotifications } from "./Context/NotificationContext";
import useAuth from "./Hooks/useAuth";
import useDeployments from "./Hooks/useDeployments";
import useBrowserNotification from "./Hooks/useBrowserNotification";
import FaviconBadge from "./Components/FaviconBadge";
import ArchivePage from "./ArchivePage";
import KnowledgeBasePage from "./KnowledgeBasePage";
import '@fortawesome/fontawesome-free/css/all.min.css';

function App() {
  return (
    <NotificationProvider>
      <MainApp />
    </NotificationProvider>
  );
}

function MainApp() {
  const { user } = useAuth(); // Assume the user context or hook is available
  const { handleDeploymentNotifications } = useNotifications(); // Now it's safe to use
  const showBrowserNotification = useBrowserNotification();
  const { deployments } = useDeployments(user, false);

  // Filter completed deployments (percentComplete === 100)
  const completedDeploymentsCount = deployments.filter(
    (deployment) => deployment.percentComplete === 100
  ).length;

  // Handle notifications for new and completed deployments
  useEffect(() => {
    if (handleDeploymentNotifications) {
      handleDeploymentNotifications(deployments, showBrowserNotification);
    }
  }, [deployments, showBrowserNotification, handleDeploymentNotifications]);

  return (
    <MenuProvider>
      <ThemeToggleProvider>
        <LocalizationProvider adapterLocale="gb" dateAdapter={AdapterDayjs}>
          <CssBaseline />
          {/* Badge for Favicon */}
          <FaviconBadge count={completedDeploymentsCount} /> {/* Only show completed deployments */}
          <Router>
            <div>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/archived-deployments"
                  element={
                    <PrivateRoute>
                      <ArchivePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/company-management"
                  element={
                    <PrivateRoute>
                      <CompanyManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/device-management"
                  element={
                    <PrivateRoute>
                      <DeviceManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deployment-logs"
                  element={
                    <AdminRoute>
                      <DeploymentLogsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/application-management"
                  element={
                    <PrivateRoute>
                      <ApplicationManagementPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/kb"
                  element={
                    <PrivateRoute>
                      <KnowledgeBasePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/statistics"
                  element={
                    <AdminRoute>
                      <StatisticsPage />
                    </AdminRoute>
                  }
                />
              </Routes>
            </div>
          </Router>
          <ToastContainer className="toastcontainer" position="top-right" />
        </LocalizationProvider>
      </ThemeToggleProvider>
    </MenuProvider>
  );
}


export default App;
